import React, { useState } from 'react';
import { ChatMessage } from '../../data_models/chat';
import useCurrentUser from '../../hooks/useCurrentUser';
import { dateToChatMessageTime } from '../../utils/dateUtils';
import useChat from '../../hooks/useChat';
import useDisplayNotifications from '../../hooks/useDisplayNotifications';
import { DisplayNotificationType } from '../../models/DisplayNotification';
import { fileSizeReadable } from '../../utils/fileUtils';
import { IoCheckmarkDoneOutline } from 'react-icons/io5';
import IconButton from '../common/IconButton';
import { HiCloudDownload, HiOutlineCloudDownload } from 'react-icons/hi';



interface MessageTextAndFileComponentProps {
    chatMessage: ChatMessage;
}

const MessageTextAndFileComponent: React.FC<MessageTextAndFileComponentProps> = ({ chatMessage }) => {

    const [downloadTargetLocation, setDownloadTargetLocation] = useState('');

    const { currentUser, currentUserType } = useCurrentUser();
    const { downloadPrivateMessageFile } = useChat();
    const { addNotification } = useDisplayNotifications();

    const [downloading, setDownloading] = useState(false);

    const [isHovered, setIsHovered] = useState(false);

    const senderIsCurrentUser = chatMessage.sender.id === currentUser?._id && chatMessage.sender.userType === currentUserType;
    const readByPartner = chatMessage.readByUsersAt.some(readBy => readBy.userId.id !== currentUser?._id || readBy.userId.userType !== currentUserType);

    const onDownload = async () => {
        try {
            if (!chatMessage.fileMeta) return;
            setDownloading(true);
            await downloadPrivateMessageFile(chatMessage.fileMeta.id,
                chatMessage.fileMeta.conversationId,
                chatMessage.fileMeta.filename,
                downloadTargetLocation);

            addNotification({
                message: 'File successfully downloaded',
                type: DisplayNotificationType.SUCCESS
            }, true);

        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            });

        } finally {
            setDownloading(false);
            setDownloadTargetLocation('');
        }
    }

    return (
        <div className={`flex ${senderIsCurrentUser ? 'justify-end ml-10' : 'justify-start mr-10'}`}
           
        >
            <div className='px-2'>
                <div className=''>
                    <div className={senderIsCurrentUser
                        ? 'bg-primary-100 px-4 py-2 rounded-bl-xl rounded-t-xl'
                        : 'text-white bg-secondary-800 px-4 py-2 rounded-br-xl rounded-t-xl'}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        {chatMessage.content
                            ? <div>
                                {chatMessage.content}
                            </div>
                            : chatMessage.fileMeta
                                ? <div className='flex flex-row space-x-4 items-center'>
                                    <div className='flex flex-col'>
                                        <p className={`text-xs font-bold`}>
                                            {senderIsCurrentUser ? 'File sent' : 'File received'}
                                        </p>
                                        <p className={`text-xs`}>
                                            {chatMessage.fileMeta.filename} ({fileSizeReadable(chatMessage.fileMeta.fileSizeBytes)})
                                        </p>
                                    </div>

                                    <IconButton
                                        theme={`${senderIsCurrentUser ? 'black' : 'white'}`}
                                        icon={<HiOutlineCloudDownload />}
                                        hoverIcon={<HiCloudDownload />}
                                        onClick={onDownload}
                                        isLoading={downloading}
                                    />

                                </div>
                                : <div>- Error -</div>}
                    </div>
                    <span className="text-xs text-gray-500">{ }</span>
                </div>

                <div className={`flex flex-row space-x-2 items-center min-w-28`}>
                    {(isHovered && readByPartner) ?
                        <p className={`text-xs ${senderIsCurrentUser ? 'ml-auto' : ''}`}>Read {dateToChatMessageTime(chatMessage?.readByUsersAt[0].readAt?.toLocaleString())} </p>
                        : <p className={`text-xs ${senderIsCurrentUser ? 'ml-auto' : ''}`}>{dateToChatMessageTime(chatMessage?._createdAt?.toLocaleString())} </p>
                    }

                    {!isHovered && senderIsCurrentUser && readByPartner &&
                        <p className='text-xs text-primary'>
                            <IoCheckmarkDoneOutline />
                        </p>}
                </div>

            </div>

        </div>
    );
};

export default MessageTextAndFileComponent;